// الإشعارات المرسلة من الخادم.
//
// تمييز مهم: تنبيهات الأذان لا تمرّ من هنا. تُجدول محلياً على الجهاز
// (انظر prayerNotifications.js) فتعمل بلا إنترنت وبدقة الثانية، ولا
// تعتمد على خادم قد يتأخر أو ينقطع. هذه للإعلانات وبدء المجالس والردود
// على الفتاوى — ما لا يمكن للجهاز أن يعرفه وحده.

import { API_BASE, isApiConfigured, getAuthToken, AUTH_CHANGED_EVENT } from '../api/client';

const isNative = () =>
  typeof window !== 'undefined' && Boolean(window.Capacitor?.isNativePlatform?.());

const plugin = () => window.Capacitor?.Plugins?.PushNotifications ?? null;

let registeredToken = null;
let devicePlatform = 'android';

/**
 * قناة الإعلانات.
 *
 * أندرويد ٨ فأحدث يُسقط الإشعار **بصمت** إن كانت قناته غير موجودة:
 * الخادم يردّ بنجاح ولا يظهر شيء على الجهاز. فالقناة تُنشأ هنا قبل
 * أي تسجيل.
 *
 * وفصلها عن قناة الأذان مقصود: من أراد تنبيه الصلاة دون إعلانات
 * المجالس يكتم هذه وحدها من إعدادات النظام، بلا أن يفقد الأذان.
 */
const ANNOUNCEMENTS_CHANNEL = {
  id: 'moslam_general',
  name: 'إعلانات المجالس',
  description: 'بدء مجلس الطيبين، والردّ على فتواك، وإعلانات الإذاعة',
  importance: 4, // يظهر فوق الشاشة لحظة وصوله
  visibility: 1, // يُقرأ على شاشة القفل
  vibration: true,
  lights: true,
};

// قنوات بأصوات يختارها المسؤول للإعلان (PUSH_SOUNDS في worker/src/push.ts).
// الصوت صفة القناة في أندرويد، وملفه في res/raw؛ وتغييره بعد إنشائها لا يسري.
const SOUND_CHANNELS = [
  { ...ANNOUNCEMENTS_CHANNEL, id: 'moslam_sound_chime', name: 'إعلانات بجرس', sound: 'notify_chime.wav' },
  { ...ANNOUNCEMENTS_CHANNEL, id: 'moslam_sound_adhan', name: 'إعلانات بصوت الأذان', sound: 'adhan_default.mp3' },
];

async function ensureChannel(Push) {
  for (const channel of [ANNOUNCEMENTS_CHANNEL, ...SOUND_CHANNELS]) {
    try {
      await Push.createChannel(channel);
    } catch {
      // iOS لا قنوات فيه، وإعادة الإنشاء على أندرويد بلا ضرر
    }
  }
}

/**
 * يربط رمز الجهاز بالحساب الحالي، أو يفكّه إن لم يكن حساب.
 *
 * التوكن في الترويسة لا الكوكي وحده: كوكي خادمنا من نطاق آخر، والآيفون
 * يحجبه داخل التطبيق، فكان الجهاز يُسجَّل بلا صاحب.
 */
async function sendToken(token) {
  if (!isApiConfigured()) return;
  registeredToken = token;
  const auth = getAuthToken();
  try {
    await fetch(`${API_BASE}/api/push/register`, {
      method: 'POST',
      // بعد الخروج لا يُرسَل كوكي جلسة قد يبقى حيّاً، فيُفكّ الجهاز فعلاً
      credentials: auth ? 'include' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
      },
      body: JSON.stringify({ token, platform: devicePlatform }),
    });
  } catch {
    // فشل التسجيل لا يعطّل التطبيق؛ يُعاد عند تغيّر الدخول أو الإقلاع القادم
  }
}

// الرمز يصل غالباً قبل أن يسجّل المستخدم دخوله، فيُربط بلا صاحب. فكل
// دخول أو خروج يعيد ربطه: المذيع يُرنّ له من لحظة دخوله، ومن خرج لا
// تصله مكالمات حسابٍ لم يعد فيه.
if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_CHANGED_EVENT, () => {
    if (registeredToken) sendToken(registeredToken);
  });
}

/**
 * يسجّل الجهاز لاستقبال الإشعارات.
 *
 * لا يطلب الإذن إن كان مرفوضاً: نظام التشغيل يتجاهل الطلب المتكرر،
 * وإزعاج المستخدم بنافذة لن تظهر بلا فائدة.
 *
 * @param {(notification: object) => void} onReceived
 */
export async function setupPush({ onReceived } = {}) {
  if (!isNative() || !isApiConfigured()) return;
  // الآيفون يعطي رمز FCM أيضاً: AppDelegate يحوّل رمز APNs عبر فايربيس
  devicePlatform = window.Capacitor?.getPlatform?.() === 'ios' ? 'ios' : 'android';

  const Push = plugin();
  if (!Push) return;

  try {
    let status = await Push.checkPermissions();
    if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
      status = await Push.requestPermissions();
    }
    if (status.receive !== 'granted') return;

    Push.addListener('registration', (t) => sendToken(t.value));

    Push.addListener('registrationError', (err) =>
      console.warn('تعذر تسجيل الجهاز للإشعارات:', err)
    );

    // إشعار وصل والتطبيق مفتوح: النظام لا يعرضه، فنمرّره للواجهة
    Push.addListener('pushNotificationReceived', (n) => onReceived?.(n));

    // المستخدم ضغط الإشعار: قد نفتح شاشة بعينها لاحقاً
    Push.addListener('pushNotificationActionPerformed', (a) => onReceived?.(a.notification));

    await ensureChannel(Push);
    await Push.register();
  } catch (err) {
    console.warn('تعذر تهيئة الإشعارات:', err);
  }
}

/** يلغي تسجيل الجهاز — يُستدعى عند تسجيل الخروج أو حذف الحساب. */
export async function teardownPush() {
  if (!registeredToken || !isApiConfigured()) return;
  try {
    await fetch(`${API_BASE}/api/push/unregister`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: registeredToken }),
    });
  } catch {
    /* تجاهل */
  }
  registeredToken = null;
}
